import { FC, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

import {
    CodeBlockLanguageTag,
    CodeBlockWrapper,
    MDXViewerWrapper
} from './styled';

interface MarkdownEditorProps {
    value: string;
    /** Read-only viewer mode (the only mode supported on the frontend). */
    readOnly?: boolean;
    /** Render the wrapper inline rather than as a block. */
    inline?: boolean;
}

interface MarkdownCodeRendererProps {
    language?: string;
    value: string;
}

const CodeRenderer: FC<MarkdownCodeRendererProps> = ({ language, value }) => (
    <CodeBlockWrapper>
        {language && <CodeBlockLanguageTag>{language}</CodeBlockLanguageTag>}
        <SyntaxHighlighter
            language={language || 'text'}
            style={vscDarkPlus}
            // react-syntax-highlighter injects its own inline styles on <pre>/<code>
            // so we override via its documented prop API rather than CSS selectors.
            customStyle={{
                margin: 0,
                padding: '12px 16px',
                background: 'transparent',
                fontSize: '13px',
                lineHeight: '1.6'
            }}
            codeTagProps={{
                style: {
                    fontFamily:
                        '"Fira Code", "Fira Mono", "Roboto Mono", monospace'
                }
            }}
        >
            {value}
        </SyntaxHighlighter>
    </CodeBlockWrapper>
);

/**
 * Read-only markdown viewer, mirroring the API of the dashboards'
 * `MarkdownEditor` (the dashboards use `@mdxeditor/editor` which requires
 * React 18+; this frontend is on React 17 so we render via
 * `react-markdown` + `react-syntax-highlighter` instead while keeping the
 * same visual treatment).
 */
const MarkdownEditor: FC<MarkdownEditorProps> = ({ value, inline }) => {
    // react-markdown@5 treats single newlines as soft breaks only when the
    // source has trailing spaces. Normalise so plain task descriptions render
    // line-by-line the way contributors authored them.
    const normalized = useMemo(
        () => (value || '').replace(/\n/gi, '  \n'),
        [ value ]
    );

    return (
        <MDXViewerWrapper inline={inline} data-color-mode="dark">
            <ReactMarkdown
                source={normalized}
                linkTarget="_blank"
                escapeHtml
                renderers={{
                    code: CodeRenderer
                }}
                transformLinkUri={(uri: string) => {
                    const urlStr = ReactMarkdown.uriTransformer(uri);
                    return urlStr.indexOf(':') === -1
                        ? `https://${urlStr}`
                        : urlStr;
                }}
            />
        </MDXViewerWrapper>
    );
};

export default MarkdownEditor;
